/**
 * Apple Pay helpers for the website Wallet checkout flow.
 *
 * Maps browser PaymentResponse payloads into IMobilePaymentRequest for the API.
 */

const APPLE_PAY_METHOD_NAME = 'apple-pay';
const DEFAULT_COUNTRY_CODE = 'US';
export type ApplePayPaymentCompleteResult = 'success' | 'fail' | 'unknown';

export interface IMobilePaymentRequest {
  methodName: string
  payerEmail: string
  payerName: string
  payerPhone: string
  requestId: string
  details: {
    token: {
      paymentData: {
        data: string
        header: Record<string, unknown>
        signature: string
        version: string
      }
      paymentMethod: {
        displayName: string
        network: string
        type: string
        transactionIdentifier?: string
      }
    }
    billingContact: {
      addressLines: string[]
      administrativeArea: string
      country: string
      countryCode: string
      familyName: string
      givenName: string
      locality: string
      phoneticFamilyName?: string
      phoneticGivenName?: string
      postalCode: string
      subLocality?: string
    }
    shippingContact: {
      emailAddress: string
      familyName: string
      givenName: string
      locality?: string
      phoneticFamilyName?: string
      phoneticGivenName?: string
      phoneNumber?: string
      postalCode?: string
      country?: string
      countryCode?: string
    }
  }
}

export interface IApplePayLineItems {
  amount: number
  fees: number
  taxes: number
}

interface ApplePayPaymentRequestOptions {
  requestPayerName?: boolean
  requestBillingAddress?: boolean
  requestPayerEmail?: boolean
  requestPayerPhone?: boolean
  requestShipping?: boolean
  shippingType?: 'shipping' | 'delivery' | 'pickup'
}

type ApplePayPaymentRequestConstructor = new (
  methodData: PaymentMethodData[],
  details: PaymentDetailsInit,
  options?: ApplePayPaymentRequestOptions
) => PaymentRequest

type IMobilePaymentBillingContact = IMobilePaymentRequest['details']['billingContact'];
type IMobilePaymentShippingContact = IMobilePaymentRequest['details']['shippingContact'];

// Form value set while the Apple Pay path is active so CC field validators can skip
export const PAYING_WITH_APPLE_PAY_FIELD = 'payingWithApplePay';

type FieldValidator = (value: any, allValues: any, meta?: any) => any;

type FormApiLike = {
  batch(fn: () => void): void
  change(name: string, value: any): void
  blur(name: string): void
  getRegisteredFields(): string[]
  getState(): { invalid?: boolean, errors?: Record<string, unknown> }
};

/**
 * Wraps a field validator so it is skipped when paying with Apple Pay
 */
export function skipWhenPayingWithApplePay(validator: FieldValidator): FieldValidator {
  return (value, allValues, meta) => {
    if (allValues?.[PAYING_WITH_APPLE_PAY_FIELD]) {
      return undefined;
    }

    return validator(value, allValues, meta);
  };
}

/**
 * Revalidates the credit card fields so they stop blocking
 */
function revalidateCreditCardFields(formApi: FormApiLike): void {
  formApi.getRegisteredFields()
    .filter((name) => name === 'cc' || name.startsWith('cc.'))
    .forEach((name) => formApi.blur(name));
}

/**
 * Mark the form as paying with Apple Pay and revalidate CC fields so they stop blocking
 */
export function prepareFormForApplePay(formApi: FormApiLike): void {
  formApi.batch(() => {
    formApi.change(PAYING_WITH_APPLE_PAY_FIELD, true);
    revalidateCreditCardFields(formApi);
  });
}

/**
 * Clear the Apple Pay payment-method flag and restore CC field validation
 */
export function clearPayingWithApplePay(formApi: FormApiLike): void {
  formApi.batch(() => {
    formApi.change(PAYING_WITH_APPLE_PAY_FIELD, false);
    revalidateCreditCardFields(formApi);
  });
}

interface IApplePayPaymentResponseDetails {
  billingContact?: Partial<IMobilePaymentBillingContact>
  shippingContact?: Partial<IMobilePaymentShippingContact>
  token?: {
    paymentData: IMobilePaymentRequest['details']['token']['paymentData']
    paymentMethod: IMobilePaymentRequest['details']['token']['paymentMethod']
  }
}

export interface ApplePayPaymentResponse extends PaymentResponse {
  readonly payerEmail: string | null
  readonly payerName: string | null
  readonly payerPhone: string | null
  readonly details: IApplePayPaymentResponseDetails | null
}

const EMPTY_BILLING_CONTACT: IMobilePaymentBillingContact = {
  addressLines: [],
  administrativeArea: '',
  country: '',
  countryCode: DEFAULT_COUNTRY_CODE,
  familyName: '',
  givenName: '',
  locality: '',
  postalCode: ''
};

function stringOrEmpty(value: string | undefined | null): string {
  return value || '';
}

function normalizeBillingContact(
  contact: Partial<IMobilePaymentBillingContact> | undefined
): IMobilePaymentBillingContact {
  const source = contact || EMPTY_BILLING_CONTACT;

  return {
    addressLines: source.addressLines || [],
    administrativeArea: stringOrEmpty(source.administrativeArea),
    country: stringOrEmpty(source.country),
    countryCode: stringOrEmpty(source.countryCode) || DEFAULT_COUNTRY_CODE,
    familyName: stringOrEmpty(source.familyName),
    givenName: stringOrEmpty(source.givenName),
    locality: stringOrEmpty(source.locality),
    phoneticFamilyName: stringOrEmpty(source.phoneticFamilyName),
    phoneticGivenName: stringOrEmpty(source.phoneticGivenName),
    postalCode: stringOrEmpty(source.postalCode),
    subLocality: stringOrEmpty(source.subLocality)
  };
}

function shippingContactFromPayer(paymentResponse: ApplePayPaymentResponse): IMobilePaymentShippingContact {
  const payerNameParts = stringOrEmpty(paymentResponse.payerName).trim().split(/\s+/).filter(Boolean);
  const givenName = payerNameParts.shift() || '';
  const familyName = payerNameParts.join(' ');

  return {
    emailAddress: stringOrEmpty(paymentResponse.payerEmail),
    familyName,
    givenName,
    phoneNumber: stringOrEmpty(paymentResponse.payerPhone)
  };
}

function normalizeShippingContact(
  contact: Partial<IMobilePaymentShippingContact> | undefined,
  paymentResponse: ApplePayPaymentResponse,
  billingContact: IMobilePaymentBillingContact
): IMobilePaymentShippingContact {
  if (!contact) {
    return shippingContactFromPayer(paymentResponse);
  }

  return {
    emailAddress: stringOrEmpty(contact.emailAddress) || stringOrEmpty(paymentResponse.payerEmail),
    familyName: stringOrEmpty(contact.familyName) || billingContact.familyName,
    givenName: stringOrEmpty(contact.givenName) || billingContact.givenName,
    locality: stringOrEmpty(contact.locality),
    phoneticFamilyName: stringOrEmpty(contact.phoneticFamilyName),
    phoneticGivenName: stringOrEmpty(contact.phoneticGivenName),
    phoneNumber: stringOrEmpty(contact.phoneNumber) || stringOrEmpty(paymentResponse.payerPhone),
    postalCode: stringOrEmpty(contact.postalCode),
    country: stringOrEmpty(contact.country),
    countryCode: stringOrEmpty(contact.countryCode)
  };
}

function parseApplePayPaymentDetails(paymentResponse: ApplePayPaymentResponse): IApplePayPaymentResponseDetails {
  if (paymentResponse.details == null || typeof paymentResponse.details !== 'object') {
    throw new Error('Apple Pay response is missing payment details');
  }

  return paymentResponse.details;
}

function assertApplePayTokenPresent(
  applePayDetails: IApplePayPaymentResponseDetails
): IApplePayPaymentResponseDetails['token'] {
  if (applePayDetails.token?.paymentData == null || applePayDetails.token.paymentMethod == null) {
    throw new Error('Apple Pay response is missing payment token data');
  }

  return applePayDetails.token;
}

export function mapPaymentResponseToMobilePaymentRequest(paymentResponse: ApplePayPaymentResponse): IMobilePaymentRequest {
  const applePayDetails = parseApplePayPaymentDetails(paymentResponse);
  const token = assertApplePayTokenPresent(applePayDetails);
  const billingContact = normalizeBillingContact(applePayDetails.billingContact);

  return {
    methodName: APPLE_PAY_METHOD_NAME,
    payerEmail: stringOrEmpty(paymentResponse.payerEmail),
    payerName: stringOrEmpty(paymentResponse.payerName),
    payerPhone: stringOrEmpty(paymentResponse.payerPhone),
    requestId: stringOrEmpty(paymentResponse.requestId),
    details: {
      token: {
        paymentData: token.paymentData,
        paymentMethod: token.paymentMethod
      },
      billingContact,
      shippingContact: normalizeShippingContact(
        applePayDetails.shippingContact,
        paymentResponse,
        billingContact
      )
    }
  };
}

export function formatApplePayAmount(value: number): string {
  if (!Number.isFinite(value)) {
    return '0.00';
  }

  return value.toFixed(2);
}

export function getApplePayGrandTotal(lineItems: IApplePayLineItems): number {
  const total = lineItems.amount + lineItems.fees + lineItems.taxes;
  return Math.round(total * 100) / 100;
}

export function createApplePayPaymentRequest(
  merchantIdentifier: string,
  description: string,
  lineItems: IApplePayLineItems
): PaymentRequest {
  const grandTotal = getApplePayGrandTotal(lineItems);
  const PaymentRequestConstructor = PaymentRequest as unknown as ApplePayPaymentRequestConstructor;

  return new PaymentRequestConstructor(
    [{
      supportedMethods: 'https://apple.com/apple-pay',
      data: {
        version: 3,
        merchantIdentifier,
        merchantCapabilities: ['supports3DS'],
        supportedNetworks: ['discover', 'masterCard', 'visa'],
        countryCode: 'US'
      }
    }],
    {
      displayItems: [
        {
          label: 'Wallet Payment',
          amount: {
            currency: 'USD',
            value: formatApplePayAmount(lineItems.amount)
          }
        },
        {
          label: 'Fees',
          amount: {
            currency: 'USD',
            value: formatApplePayAmount(lineItems.fees)
          }
        },
        {
          label: 'Taxes',
          amount: {
            currency: 'USD',
            value: formatApplePayAmount(lineItems.taxes)
          }
        }
      ],
      total: {
        label: description,
        amount: {
          value: formatApplePayAmount(grandTotal),
          currency: 'USD'
        }
      }
    },
    {
      requestPayerName: true,
      requestBillingAddress: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
      requestShipping: false,
      shippingType: 'shipping'
    }
  );
}


/**
 * Tell Apple that the Payment Request sheet finished. Must be called after authorize,
 * once the merchant has accepted or rejected the charge.
 */
export async function completeApplePayPaymentResponse(
  paymentResponse: PaymentResponse,
  result: ApplePayPaymentCompleteResult
): Promise<void> {
  try {
    await paymentResponse.complete(result);
  } catch (error) {
    if (typeof console !== 'undefined' && typeof console.error === 'function') {
      console.error('Apple Pay paymentResponse.complete failed', error);
    }
  }
}
